import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  TemplateRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Paging } from 'src/app/api/types/Pageable';
import { DataTableCellDirective } from './data-table-cell.directive';

export type DataTableAlign = 'left' | 'right' | 'center';
export type SortDirection = 'asc' | 'desc' | '';

/**
 * One column of an <app-data-table>. Text columns need only `key` (+ `header`);
 * anything richer (image, icons, buttons, pipes) is drawn by a matching
 * `<ng-template appDataTableCell="key">`. Set `isActions` for the trailing
 * action cell — it is excluded from the mobile label/value list and rendered in
 * the card's action row instead.
 */
export interface DataTableColumn {
  /** Unique key; also the row property read when no `value`/template is given. */
  key: string;
  /** Translate key for the <th> and the mobile card label. Omit for a blank header. */
  header?: string;
  align?: DataTableAlign;
  sortable?: boolean;
  /** Explicit column width, e.g. '24%' or '132px'. */
  width?: string;
  headerClass?: string;
  cellClass?: string;
  /** Drop this column from the mobile card layout. */
  hideOnMobile?: boolean;
  /** Hide this column on the desktop table below the `lg` breakpoint. */
  hideBelowLg?: boolean;
  /** Marks the trailing actions column (mobile: rendered in the action row). */
  isActions?: boolean;
  /** Plain-text accessor when the value is not simply `row[key]`. */
  value?: (row: unknown) => unknown;
}

/**
 * Reusable admin data table: a Bootstrap `.table` on desktop and stacked
 * label/value cards on mobile, driven by a `columns` config plus optional
 * per-column `<ng-template appDataTableCell>` cell templates. Ships the shared
 * list visuals (inactive-row grey, clickable-row cursor, empty state) once and
 * composes `app-pager`, so screens stop hand-writing a `<table>` + `d-md-none`
 * card twin for every records list.
 *
 * Rows are already-mapped view models (the parent formats dates/prices/enums),
 * matching the existing app-voucher-list / app-loyalty-rule-list pattern.
 */
@Component({
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataTableComponent implements AfterContentInit, OnDestroy {
  @Input() columns: DataTableColumn[] = [];
  @Input() rows: unknown[] = [];
  /** False shows the loading panel instead of the table. */
  @Input() isLoaded = true;
  /** Translate key for the no-rows message. */
  @Input() emptyText = 'admin.global.noData';
  @Input() clickableRows = false;
  /** Render the responsive mobile card layout below `md`. */
  @Input() mobileCards = true;
  /** Fixed table layout + full width (honours per-column `width`). */
  @Input() tableLayoutFixed = false;

  @Input() showPager = false;
  @Input() totalPages = 0;
  @Input() pageNumber = 0;
  @Input() pageSize = 20;
  @Input() pagerVariant: 'default' | 'theme' = 'default';

  /** Row-selection checkboxes with a header select-all. */
  @Input() selectable = false;
  @Input() selectedKeys: unknown[] = [];

  /** Identity for trackBy / selection (defaults to `row.id`). */
  @Input() rowKey: (row: unknown) => unknown = (row) =>
    (row as { id?: unknown })?.id;
  /** Greys a row (defaults to `row.inactive`). */
  @Input() rowInactive: (row: unknown) => boolean = (row) =>
    !!(row as { inactive?: boolean })?.inactive;
  /** Optional per-row data-testid (desktop row + mobile card unless overridden). */
  @Input() rowTestId?: (row: unknown) => string;
  /** Optional distinct data-testid for the mobile card (falls back to `rowTestId`). */
  @Input() mobileRowTestId?: (row: unknown) => string;
  /** data-testid on the empty-state element. */
  @Input() emptyTestId = 'data-table-empty';

  /** Controlled sort state (the parent sorts the data and reflects it here). */
  @Input() sortKey = '';
  @Input() sortDirection: SortDirection = '';

  @Output() rowClick = new EventEmitter<unknown>();
  @Output() paginate = new EventEmitter<Paging>();
  @Output() sortChange = new EventEmitter<{
    key: string;
    direction: SortDirection;
  }>();
  @Output() selectionChange = new EventEmitter<unknown[]>();

  @ContentChildren(DataTableCellDirective)
  private cellDirectives!: QueryList<DataTableCellDirective>;

  private templates = new Map<string, TemplateRef<unknown>>();
  private destroyed$ = new Subject<void>();

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterContentInit(): void {
    this.indexTemplates();
    this.cellDirectives.changes
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.indexTemplates();
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private indexTemplates(): void {
    this.templates.clear();
    this.cellDirectives.forEach((dir) =>
      this.templates.set(dir.key, dir.template),
    );
  }

  templateFor(key: string): TemplateRef<unknown> | null {
    return this.templates.get(key) ?? null;
  }

  cellValue(column: DataTableColumn, row: unknown): unknown {
    if (column.value) {
      return column.value(row);
    }
    return (row as Record<string, unknown>)?.[column.key];
  }

  /** Columns shown as label/value pairs in a mobile card (no actions column). */
  get mobileColumns(): DataTableColumn[] {
    return this.columns.filter((c) => !c.hideOnMobile && !c.isActions);
  }

  get actionsColumn(): DataTableColumn | null {
    return this.columns.find((c) => c.isActions) ?? null;
  }

  trackBy = (index: number, row: unknown): unknown =>
    this.rowKey ? this.rowKey(row) : index;

  onRowClick(row: unknown): void {
    if (this.clickableRows) {
      this.rowClick.emit(row);
    }
  }

  onHeaderClick(column: DataTableColumn): void {
    if (!column.sortable) {
      return;
    }
    let direction: SortDirection = 'asc';
    if (this.sortKey === column.key) {
      direction =
        this.sortDirection === 'asc'
          ? 'desc'
          : this.sortDirection === 'desc'
            ? ''
            : 'asc';
    }
    this.sortKey = direction ? column.key : '';
    this.sortDirection = direction;
    this.sortChange.emit({ key: this.sortKey, direction });
  }

  sortIcon(column: DataTableColumn): string {
    if (this.sortKey !== column.key || !this.sortDirection) {
      return 'fa-sort';
    }
    return this.sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
  }

  isSelected(row: unknown): boolean {
    return this.selectedKeys.includes(this.rowKey(row));
  }

  get allSelected(): boolean {
    return (
      this.rows.length > 0 && this.rows.every((row) => this.isSelected(row))
    );
  }

  toggleRow(row: unknown, event: Event): void {
    event.stopPropagation();
    const key = this.rowKey(row);
    const next = new Set(this.selectedKeys);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.selectedKeys = [...next];
    this.selectionChange.emit(this.selectedKeys);
  }

  toggleAll(): void {
    this.selectedKeys = this.allSelected
      ? []
      : this.rows.map((row) => this.rowKey(row));
    this.selectionChange.emit(this.selectedKeys);
  }

  onPaginate(paging: Paging): void {
    this.paginate.emit(paging);
  }
}
